import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import Icon from '../../components/AppIcon';
import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import LanguageSwitcher from '../../components/ui/LanguageSwitcher';
import { useTranslation } from '../../hooks/useTranslation';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;
const RAZORPAY_KEY = 'rzp_live_SyswhY6CmR3gMx';
const GRAD = 'linear-gradient(135deg,#f97316,#ea580c)';
const DO_FINANCE_VIDEO_URL = 'https://dfliwixthxiocdfzpbvt.supabase.co/storage/v1/object/public/assets/do-finance-video.mp4';

// Maps DB current_screen value → frontend screen number
const SCREEN_MAP = {
  gst_otp: 2,
  gstin: 2,
  gstin_confirm: 3,
  pans_mobile: 4,
  bank_account: 5,
  consent: 6,
};

// Verification items shown during pipeline spinner
const PIPELINE_ITEMS = [
  { key: 'pan',      icon: 'CreditCard', label: 'PAN Identity' },
  { key: 'gstin',    icon: 'Building2',  label: 'GSTIN Verification' },
  { key: 'gstr3b',   icon: 'FileText',   label: 'GSTR-3B Compliance' },
  { key: 'company',  icon: 'TrendingUp', label: 'Company Intelligence' },
  { key: 'social',   icon: 'Smartphone', label: 'Social Intelligence' },
  { key: 'equifax',  icon: 'BarChart2',  label: 'Equifax Bureau' },
  { key: 'trade',    icon: 'Package',    label: 'SugarHathi Record' },
  { key: 'verdict',  icon: 'CheckCircle',label: 'Verdict' },
];

const UNSUPPORTED_KEYWORDS = ['public limited', 'government', 'trust', 'society', 'huf', 'statutory'];

const spin = `@keyframes sh-spin { to { transform: rotate(360deg) } }`;
const dofResponsive = `
.dof-wrap { max-width: 480px; margin: 0 auto; }
.dof-wrap-flush { max-width: 480px; margin: 0 auto; }
@media (min-width: 768px) {
  .dof-wrap { max-width: 680px; }
  .dof-wrap-flush { max-width: 680px; }
}
@media (min-width: 1100px) {
  .dof-wrap { max-width: 860px; }
  .dof-wrap-flush { max-width: 860px; }
}
.dof-hub-cards { display: flex; flex-direction: column; gap: 14px; }
@media (min-width: 768px) {
  .dof-hub-cards { flex-direction: row; align-items: stretch; }
  .dof-hub-cards > button { flex: 1 1 0; }
}
`;

// ── Shared UI atoms ───────────────────────────────────────────────────────────

const Spinner = ({ size = 28 }) => (
  <div style={{
    width: size, height: size, borderRadius: '50%',
    border: '3px solid #f97316', borderTopColor: 'transparent',
    animation: 'sh-spin 0.8s linear infinite',
  }} />
);

const fmtScore = (n) => (n != null ? Math.round(n) : '—');

const maskPan = (pan) => {
  if (!pan || pan.length < 10) return 'XXXXXXXXXX';
  return 'XXXXX' + pan.slice(5);
};

const getRoleFromConstitution = (c) => {
  if (!c) return 'proprietor';
  const cl = c.toLowerCase();
  if (cl.includes('proprietor')) return 'proprietor';
  if (cl.includes('private') || cl.includes('opc')) return 'director';
  if (cl.includes('llp')) return 'designated_partner';
  return 'partner';
};

const Row = ({ label, value }) => (
  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid #f9fafb' }}>
    <span style={{ fontSize: 12, color: '#6b7280' }}>{label}</span>
    <span style={{ fontSize: 13, fontWeight: 600, color: '#1f2937', textAlign: 'right', maxWidth: '55%' }}>{value ?? '—'}</span>
  </div>
);

const Tag = ({ children, color = '#ea580c', bg = '#fff7ed' }) => (
  <span style={{ padding: '3px 8px', borderRadius: 6, background: bg, color, fontSize: 11, fontWeight: 700, display: 'inline-block' }}>
    {children}
  </span>
);

const ScoreDial = ({ label, score, max, band, empty }) => {
  const r = 34;
  const circ = 2 * Math.PI * r;
  const pct = empty || !score ? 0 : Math.min(1, score / max);
  const dash = circ * pct;
  const color = !score || empty ? '#d1d5db'
    : pct > 0.7 ? '#16a34a'
    : pct > 0.4 ? '#d97706'
    : '#dc2626';
  return (
    <div style={{ flex: 1, background: '#fff', borderRadius: 14, border: '1.5px solid #f3f4f6', padding: '14px 10px', textAlign: 'center' }}>
      <p style={{ fontSize: 11, fontWeight: 600, color: '#6b7280', marginBottom: 8 }}>{label}</p>
      <div style={{ position: 'relative', width: 80, height: 80, margin: '0 auto 8px' }}>
        <svg width="80" height="80" viewBox="0 0 80 80">
          <circle cx="40" cy="40" r={r} fill="none" stroke="#f3f4f6" strokeWidth="8" />
          <circle cx="40" cy="40" r={r} fill="none" stroke={color} strokeWidth="8"
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeLinecap="round"
            transform="rotate(-90 40 40)"
            style={{ transition: 'stroke-dasharray 1.2s ease' }}
          />
        </svg>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <span style={{ fontSize: empty ? 18 : 20, fontWeight: 800, color }}>
            {empty ? '?' : fmtScore(score)}
          </span>
        </div>
      </div>
      <p style={{ fontSize: 12, fontWeight: 600, color, margin: 0 }}>{empty ? 'Pending' : (band ?? '—')}</p>
    </div>
  );
};

// Segmented speedometer-style semicircle gauge with needle — used on the Verdict screen
const SEMI_GAUGE_BANDS = ['#dc2626', '#f97316', '#facc15', '#a3e635', '#16a34a'];
const semiPolar = (cx, cy, r, angleDeg) => {
  const rad = (angleDeg * Math.PI) / 180;
  return { x: cx + r * Math.cos(rad), y: cy - r * Math.sin(rad) };
};
const semiArcPath = (cx, cy, r, startAngle, endAngle) => {
  const start = semiPolar(cx, cy, r, startAngle);
  const end = semiPolar(cx, cy, r, endAngle);
  const largeArc = Math.abs(startAngle - endAngle) > 180 ? 1 : 0;
  return `M ${start.x} ${start.y} A ${r} ${r} 0 ${largeArc} 1 ${end.x} ${end.y}`;
};
const SemiGauge = ({ label, value, max, band, empty }) => {
  const pct = empty || !value ? 0 : Math.max(0, Math.min(1, value / max));
  const cx = 100, cy = 100, r = 80;
  const needleAngle = 180 - pct * 180;
  const needleTip = semiPolar(cx, cy, r - 28, needleAngle);
  const bandWidth = 180 / SEMI_GAUGE_BANDS.length;
  const bandColor = empty || !value ? '#d1d5db' : SEMI_GAUGE_BANDS[Math.min(SEMI_GAUGE_BANDS.length - 1, Math.floor(pct * SEMI_GAUGE_BANDS.length))];
  return (
    <div style={{ flex: 1, background: '#fff', borderRadius: 18, border: '1.5px solid #f3f4f6', padding: '16px 10px 14px', textAlign: 'center' }}>
      <p style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.03em' }}>{label}</p>
      <div style={{ position: 'relative', width: '100%', maxWidth: 150, margin: '0 auto' }}>
        <svg viewBox="0 0 200 115" width="100%">
          {SEMI_GAUGE_BANDS.map((c, i) => {
            const startAngle = 180 - i * bandWidth;
            const endAngle = 180 - (i + 1) * bandWidth;
            return <path key={i} d={semiArcPath(cx, cy, r, startAngle, endAngle)} fill="none" stroke={empty ? '#e5e7eb' : c} strokeWidth="24" strokeLinecap="butt" />;
          })}
          <line x1={cx} y1={cy} x2={needleTip.x} y2={needleTip.y} stroke="#1c1917" strokeWidth="3.5" strokeLinecap="round" style={{ transition: 'all 1s ease' }} />
          <circle cx={cx} cy={cy} r="7" fill="#1c1917" />
        </svg>
        <div style={{ marginTop: -6 }}>
          <span style={{ fontSize: 22, fontWeight: 800, color: '#1c1917' }}>{empty ? '?' : fmtScore(value)}</span>
          <span style={{ fontSize: 11, color: '#a8a29e' }}>/{max}</span>
        </div>
      </div>
      <p style={{ fontSize: 12, fontWeight: 700, color: bandColor, margin: '4px 0 0' }}>{empty ? 'Pending' : (band ?? '—')}</p>
    </div>
  );
};

const DossierBlock = ({ icon, title, visible, children }) => (
  <div style={{
    background: '#fff', borderRadius: 14, border: '1.5px solid #f3f4f6',
    padding: 16, marginBottom: 12,
    opacity: visible ? 1 : 0,
    transform: visible ? 'translateY(0)' : 'translateY(14px)',
    transition: 'opacity 0.4s ease, transform 0.4s ease',
  }}>
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: children ? 12 : 0 }}>
      <div style={{ width: 34, height: 34, borderRadius: 10, background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Icon name={icon} size={17} color="#ea580c" />
      </div>
      <p style={{ fontWeight: 700, fontSize: 14, color: '#1f2937', margin: 0 }}>{title}</p>
    </div>
    {children}
  </div>
);

const USP_POINTS = [
  { key: 'cheque',  icon: 'Landmark',  text: 'No cheques needed' },
  { key: 'collateral', icon: 'ShieldOff', text: 'No collateral required' },
  { key: 'branch',  icon: 'Home',      text: 'No branch visits' },
  { key: 'paper',   icon: 'FileX',     text: 'No physical paperwork' },
  { key: 'guarantor', icon: 'Users',   text: 'No guarantor needed' },
  { key: 'hidden',  icon: 'Receipt',   text: 'No hidden charges' },
];

const UspIntro = ({ onInterested }) => {
  const [visibleCount, setVisibleCount] = useState(0);

  useEffect(() => {
    if (visibleCount >= USP_POINTS.length) return;
    const t = setTimeout(() => setVisibleCount((c) => c + 1), 450);
    return () => clearTimeout(t);
  }, [visibleCount]);

  const allShown = visibleCount >= USP_POINTS.length;

  return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <style>{dofResponsive}</style>
      <div className="dof-wrap" style={{ padding: '36px 20px 40px', display: 'flex', flexDirection: 'column' }}>
        <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', color: '#c2410c', textTransform: 'uppercase', marginBottom: 6 }}>
          DO Finance
        </p>
        <h1 style={{ fontSize: 23, fontWeight: 700, color: '#1c1917', margin: 0, marginBottom: 28, lineHeight: 1.3 }}>
          Finance made for the Sugar Trade
        </h1>

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {USP_POINTS.map((p, i) => {
            const shown = i < visibleCount;
            return (
              <div
                key={p.key}
                style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  width: '100%', padding: '15px 18px', borderRadius: 999,
                  background: '#fff', border: '1.5px solid #f0e9e0',
                  opacity: shown ? 1 : 0,
                  transform: shown ? 'translateY(0)' : 'translateY(10px)',
                  transition: 'opacity 0.45s ease, transform 0.45s ease',
                }}
              >
                <div style={{
                  width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: '#fff7ed',
                }}>
                  <Icon name={p.icon} size={16} color="#ea580c" strokeWidth={2} />
                </div>
                <p style={{ fontSize: 14, fontWeight: 800, color: '#1c1917', margin: 0 }}>{p.text}</p>
              </div>
            );
          })}
        </div>

        <button
          onClick={onInterested}
          disabled={!allShown}
          style={{
            width: '100%', padding: '16px', borderRadius: 14, border: 'none',
            background: allShown ? GRAD : '#e7e5e4',
            color: allShown ? '#fff' : '#a8a29e',
            fontSize: 15, fontWeight: 700,
            cursor: allShown ? 'pointer' : 'default',
            marginTop: 28,
            transition: 'background 0.4s ease, color 0.4s ease',
          }}
        >
          I'm Interested
        </button>
      </div>
    </div>
  );
};

// ═════════════════════════════════════════════════════════════════════════════
// Main component
// ═════════════════════════════════════════════════════════════════════════════

const DoFinanceApply = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  // Phase: checking | hero | collection | pipeline | live_dossier | returning | evaluating | failed
  const [phase, setPhase] = useState('checking');
  // Within 'hero' phase: usp (pitch) shown first, then existing hero content
  const [heroStep, setHeroStep] = useState('usp');
  const [beforeApplyOpen, setBeforeApplyOpen] = useState(false);
  const [beforeApplyConfirmed, setBeforeApplyConfirmed] = useState(false);
  const [videoNotice, setVideoNotice] = useState(false);

  // User profile
  const [profile, setProfile] = useState(null);

  // Collection state
  const [screen, setScreen]           = useState(1);
  const [applicationId, setApplicationId] = useState(null);

  // Screen 1 — Payment
  const [payLoading, setPayLoading]   = useState(false);

  // Screen 3 — GSTIN confirm
  const [gstinData, setGstinData]     = useState(null);
  const [gstinLoading, setGstinLoading] = useState(false);
  const [gstinError, setGstinError] = useState('');
  const [constitution, setConstitution] = useState('');

  // Screen 4 — PANs
  const [pansList, setPansList]       = useState([{ pan: '', is_primary: true, mobile: '' }]);
  const [verifiedPan, setVerifiedPan] = useState('');
  const [verifiedPanName, setVerifiedPanName] = useState('');
  const [panVerifying, setPanVerifying] = useState(false);
  const [panVerifyError, setPanVerifyError] = useState('');

  // Screen 4 (Partnership constitution) — GST Document upload, routes to admin review queue
  const [partnershipFile, setPartnershipFile] = useState(null);
  const [partnershipUploading, setPartnershipUploading] = useState(false);
  const [partnershipError, setPartnershipError] = useState('');
  const [partnershipReviewStatus, setPartnershipReviewStatus] = useState(null);

  // Screen 5 — Bank
  const [bankAccount, setBankAccount] = useState('');
  const [bankIfsc, setBankIfsc]       = useState('');
  const [bankVerifyLoading, setBankVerifyLoading] = useState(false);
  const [bankVerifyResult, setBankVerifyResult] = useState(null);
  const [bankVerifyError, setBankVerifyError] = useState('');

  // Screen 6 — Consent
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);

  // General
  const [colError, setColError]       = useState('');
  const [pipelineStep, setPipelineStep] = useState('');

  // Dossier
  const [dossierData, setDossierData] = useState({});
  const [visibleKeys, setVisibleKeys] = useState([]);
  const [verdict, setVerdict]         = useState(null);
  const [failedReason, setFailedReason] = useState('');
  const [evalStep, setEvalStep]       = useState('');
  const [recheckLoading, setRecheckLoading] = useState(false);

  // Hero animation
  const [heroAnimKeys, setHeroAnimKeys] = useState([]);
  const heroTimers = useRef([]);

  // ── Bootstrap ──────────────────────────────────────────────────────────────

  useEffect(() => {
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.async = true;
    document.body.appendChild(s);
    // Restore from sessionStorage instantly to avoid spinner on re-focus
    try {
      const cached = sessionStorage.getItem('doFinanceState');
      if (cached) {
        const st = JSON.parse(cached);
        if (st.applicationId && st.phase === 'collection') {
          setApplicationId(st.applicationId);
          setScreen(st.screen ?? 2);
          setPhase('collection');
          checkStatus(); // silent background validation
          return () => {
            heroTimers.current.forEach(clearTimeout);
            if (document.body.contains(s)) document.body.removeChild(s);
          };
        }
      }
    } catch {}
    checkStatus();
    return () => {
      heroTimers.current.forEach(clearTimeout);
      if (document.body.contains(s)) document.body.removeChild(s);
    };
  }, []);

  // Hero card stagger animation — runs whenever we enter hero phase
  useEffect(() => {
    heroTimers.current.forEach(clearTimeout);
    heroTimers.current = [];
    if (phase !== 'hero') return;
    setHeroAnimKeys([]);
    ['c0', 'c1', 'c2', 'c3'].forEach((k, i) => {
      heroTimers.current.push(
        setTimeout(() => setHeroAnimKeys(prev => [...prev, k]), 400 + i * 550)
      );
    });
  }, [phase]);

  // Persist collection state to sessionStorage so re-focus skips spinner
  useEffect(() => {
    if (!applicationId) return;
    if (phase === 'collection') {
      try {
        sessionStorage.setItem('doFinanceState', JSON.stringify({
          applicationId, screen, phase,
        }));
      } catch {}
    } else if (phase === 'returning' || phase === 'evaluating' || phase === 'failed' || phase === 'hero') {
      try { sessionStorage.removeItem('doFinanceState'); } catch {}
    }
  }, [applicationId, screen, phase]);

  // ── checkStatus ────────────────────────────────────────────────────────────

  const checkStatus = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) { navigate('/login-authentication'); return; }

      const { data: prof } = await supabase
        .from('user_profiles')
        .select('user_id, full_name, gstin, role, mobile_phone')
        .eq('user_id', session.user.id)
        .single();
      setProfile(prof);

      // Employee-linked accounts never get DO Finance, regardless of how
      // they arrive here — the dashboard hides/blocks the button, but a
      // direct URL visit would otherwise skip that check entirely.
      const { data: link } = await supabase
        .from('business_account_links')
        .select('relation_type')
        .eq('linked_user_id', session.user.id)
        .eq('status', 'active')
        .maybeSingle();
      if (link?.relation_type === 'employee') {
        navigate('/buyer-operations-dashboard');
        return;
      }

      // Partnership applicants are routed to the admin_review_requests queue
      // (Step 8) instead of the automated trader_od_applications pipeline —
      // check this first, since a submitted review request never changes
      // trader_od_applications.status away from 'payment_completed'.
      const { data: prReq } = await supabase
        .from('admin_review_requests')
        .select('status, decision_notes')
        .eq('context', 'do_finance_partnership_doc')
        .eq('requested_by_user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (prReq) {
        setPartnershipReviewStatus(prReq);
        setPhase(
          prReq.status === 'pending' ? 'partnership_pending' :
          prReq.status === 'approved' ? 'partnership_approved' :
          'partnership_rejected'
        );
        return;
      }

      const resp = await fetch(`${SUPABASE_URL}/functions/v1/do-finance-eligibility`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
      });

      const ct = resp.headers.get('content-type') || '';

      if (ct.includes('text/event-stream')) {
        // Returning user — scored / approved / activated → stream dossier
        const blocks = {};
        let v = null;
        await readSSE(resp, (blk) => {
          if (blk.block === 'verdict') v = blk.data;
          else if (blk.block && blk.block !== 'error') blocks[blk.block] = blk.data;
        });
        setDossierData(blocks);
        setVerdict(v);
        const keys = Object.keys(blocks);
        keys.forEach((k, i) => setTimeout(() => setVisibleKeys(prev => [...prev, k]), i * 100));
        if (v) setTimeout(() => setVisibleKeys(prev => [...prev, 'verdict']), keys.length * 100);
        setPhase('returning');
        return;
      }

      const json = await resp.json();

      if (json.status === 'no_application') {
        setPhase('hero');
        return;
      }

      if (json.status === 'in_progress') {
        // Payment done — resume at the correct screen
        setApplicationId(json.application_id);
        const resumeScreen = SCREEN_MAP[json.current_screen] ?? 2;
        setScreen(resumeScreen);
        // Unified checklist (screens 2-4): restore GSTIN data from server state
        if (['gst_otp', 'gstin', 'gstin_confirm', 'pans_mobile'].includes(json.current_screen) && prof?.gstin) {
          loadGstinData(prof.gstin);
        }
        setPhase('collection');
        return;
      }

      if (json.status === 'evaluating') {
        setEvalStep(json.step ?? 'started');
        setPhase('evaluating');
        return;
      }

      if (json.status === 'evaluation_failed') {
        setFailedReason(json.reason ?? 'Evaluation could not be completed.');
        setPhase('failed');
        return;
      }

      setPhase('hero');
    } catch (err) {
      console.error('checkStatus error:', err);
      setPhase('hero');
    }
  };

  // ── SSE reader ─────────────────────────────────────────────────────────────

  const readSSE = async (resp, onBlock) => {
    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let buf = '';
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buf += decoder.decode(value, { stream: true });
      const parts = buf.split('\n\n');
      buf = parts.pop();
      for (const part of parts) {
        if (part.startsWith('data: ')) {
          try {
            const d = JSON.parse(part.slice(6));
            if (d.block !== 'done') onBlock(d);
          } catch {}
        }
      }
    }
  };

  // ── GSTIN loader ───────────────────────────────────────────────────────────

  const loadGstinData = async (gstin) => {
    setGstinLoading(true);
    setGstinError('');
    try {
      const { data, error } = await supabase.functions.invoke('verify-gstin', { body: { gstin } });
      if (error || !data?.success) {
        setGstinError(data?.error || error?.message || 'Could not verify GSTIN. Please retry.');
        return;
      }
      setGstinData(data);
      const con = data.constitution || '';
      setConstitution(con);
      setPansList([{ pan: '', is_primary: true, mobile: '' }]);
    } catch (err) {
      setGstinError(err?.message || 'Could not verify GSTIN. Please retry.');
    } finally { setGstinLoading(false); }
  };

  // Auto-fetch GSTIN once the checklist screens are reached — replaces the
  // old GST-portal-OTP step. Fires regardless of entry path (fresh payment,
  // resume, or the "Continue" button on an already-paid application).
  useEffect(() => {
    if (phase !== 'collection' || screen < 2 || screen > 4) return;
    if (gstinData || gstinLoading || gstinError) return;
    if (!profile) return; // profile still loading — wait
    if (!profile.gstin) {
      setGstinError('No GSTIN found on your profile. Please update your KYC details before applying, or contact support.');
      return;
    }
    loadGstinData(profile.gstin).then(() => {
      saveProgress('gstin', {});
    });
  }, [phase, screen, gstinData, gstinLoading, gstinError, profile]);

  // ── saveProgress (non-fatal helper) ───────────────────────────────────────

  const saveProgress = async (nextScreen, screenData) => {
    try {
      await supabase.functions.invoke('do-finance-save-progress', {
        body: { application_id: applicationId, current_screen: nextScreen, screen_data: screenData },
      });
    } catch (err) {
      console.warn('saveProgress non-fatal:', err);
    }
  };

  // ── Screen 1: Payment ──────────────────────────────────────────────────────

  const handlePayment = async () => {
    setColError('');
    setPayLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('do-finance-create-order', {
        body: { amount_paise: 117900, purpose: 'do_finance_assessment' },
      });
      if (error || !data?.order_id) throw new Error('Could not create payment order. Please try again.');
      const options = {
        key: RAZORPAY_KEY,
        amount: data.amount,
        currency: data.currency || 'INR',
        name: 'SugarHathi',
        description: 'DO Finance Assessment Fee',
        order_id: data.order_id,
        handler: async (response) => {
          await handlePaymentVerify(
            response.razorpay_order_id,
            response.razorpay_payment_id,
            response.razorpay_signature,
          );
        },
        prefill: { contact: profile?.mobile_phone || '' },
        theme: { color: '#f97316' },
        modal: { ondismiss: () => setPayLoading(false) },
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    } catch (err) {
      setColError(err.message || 'Payment failed. Try again.');
      setPayLoading(false);
    }
  };

  const handlePaymentVerify = async (orderId, paymentId, signature) => {
    try {
      const { data: vd, error: ve } = await supabase.functions.invoke('verify-razorpay-payment', {
        body: {
          razorpay_order_id: orderId,
          razorpay_payment_id: paymentId,
          razorpay_signature: signature,
          purpose: 'do_finance_assessment',
        },
      });
      if (ve || !vd?.success) throw new Error('Payment verification failed. Please contact support.');
      setApplicationId(vd.application_id);
      setColError('');
      setScreen(2);
    } catch (err) {
      setColError(err.message || 'Payment verification failed. Please contact support.');
    } finally {
      setPayLoading(false);
    }
  };

  // ── Screen 4: PANs ─────────────────────────────────────────────────────────

  const handlePansNext = async () => {
    const primary = pansList.find(p => p.is_primary) || pansList[0];
    if (!primary?.pan || primary.pan.length !== 10) { setColError('Please enter a valid 10-character PAN'); return; }
    if (!primary?.mobile || primary.mobile.length !== 10) { setColError('Please enter a valid 10-digit mobile number'); return; }
    setColError('');
    await saveProgress('bank_account', { pans: pansList });
    setScreen(5);
  };

  // ── Screen 4 (Partnership): GST Document upload → admin review queue ───────

  const handlePartnershipSubmit = async () => {
    if (!partnershipFile) { setPartnershipError('Please select your GST document to upload.'); return; }
    setPartnershipError('');
    setPartnershipUploading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      const path = `${user.id}/${applicationId || Date.now()}_gst_doc_${partnershipFile.name}`;

      const { error: upErr } = await supabase.storage
        .from('review-documents')
        .upload(path, partnershipFile);
      if (upErr) throw new Error(upErr.message || 'Could not upload document. Please retry.');

      const { error: insErr } = await supabase.from('admin_review_requests').insert({
        context: 'do_finance_partnership_doc',
        requested_by_user_id: user.id,
        status: 'pending',
        urgency: 'urgent',
        document_paths: [path],
        payload: {
          gstin: profile?.gstin || '',
          legal_name: gstinData?.legal_name || '',
          applicant_name: profile?.full_name || '',
          mobile: profile?.mobile_phone || '',
        },
      });
      if (insErr) throw new Error(insErr.message || 'Could not submit your request. Please retry.');

      setPartnershipReviewStatus({ status: 'pending', decision_notes: null });
      setPhase('partnership_pending');
    } catch (err) {
      setPartnershipError(err.message || 'Could not submit your document. Please retry.');
    } finally {
      setPartnershipUploading(false);
    }
  };

  // ── Screen 5: Bank ─────────────────────────────────────────────────────────

  const handleBankVerify = async () => {
    if (!bankAccount) { setColError('Please enter your account number'); return; }
    if (!bankIfsc || bankIfsc.length !== 11) { setColError('Please enter a valid 11-character IFSC code'); return; }
    setColError('');
    setBankVerifyError('');
    setBankVerifyLoading(true);
    try {
      const gstinName = gstinData?.trade_name || gstinData?.legal_name || '';
      const { data, error } = await supabase.functions.invoke('do-finance-bank-verify', {
        body: { application_id: applicationId, account_number: bankAccount, ifsc: bankIfsc, gstin_name: gstinName },
      });
      if (error || !data?.success) throw new Error(data?.error || 'Could not verify this account.');
      if (!data.account_exists) throw new Error(data.message || 'This account could not be verified. Please check the details.');
      setBankVerifyResult(data);
    } catch (err) {
      setBankVerifyError(err.message || 'Could not verify this account.');
      setBankVerifyResult(null);
    } finally {
      setBankVerifyLoading(false);
    }
  };

  const handleBankNext = async () => {
    if (!bankAccount) { setColError('Please enter your account number'); return; }
    if (!bankIfsc || bankIfsc.length !== 11) { setColError('Please enter a valid 11-character IFSC code'); return; }
    setColError('');
    await saveProgress('consent', { bank_account_number: bankAccount, bank_ifsc: bankIfsc });
    setScreen(6);
    handleBankVerify();
  };

  // ── Screen 6: Consent + submit pipeline ────────────────────────────────────

  const handleSubmit = async () => {
    if (!c1 || !c2) { setColError('Please accept both declarations to proceed'); return; }
    setColError('');
    setPhase('pipeline');
    setPipelineStep('Saving your consent...');
    try {
      await saveProgress('scoring', { c1, c2 });
      setPipelineStep('Running 13 verifications. This may take up to 60 seconds...');

      const primary = pansList.find(p => p.is_primary) || pansList[0];
      const others  = pansList.filter(p => !p.is_primary);

      const { data: result, error: pipeErr } = await supabase.functions.invoke('trader-od-apply', {
        body: {
          application_id: applicationId,
          gstin: profile?.gstin,
          pan_1: primary.pan.toUpperCase(),
          pan_2: others[0]?.pan?.toUpperCase() || null,
          pan_3: others[1]?.pan?.toUpperCase() || null,
          pan_4: others[2]?.pan?.toUpperCase() || null,
          bank_account_number: bankAccount,
          bank_ifsc: bankIfsc.toUpperCase(),
          requested_limit_amount: 500000,
          mobile: primary.mobile || profile?.mobile_phone || '',
          applicant_role: profile?.role || 'buyer',
          name: profile?.full_name || '',
          constitution,
        },
      });

      if (pipeErr || !result?.application_id) {
        throw new Error(pipeErr?.message || result?.error || 'Evaluation could not be completed.');
      }

      // Persist PANs
      const { data: { user } } = await supabase.auth.getUser();
      for (let i = 0; i < pansList.length; i++) {
        const p = pansList[i];
        if (p.pan.length === 10) {
          await supabase.from('do_finance_applicant_pans').insert({
            application_id: applicationId,
            user_id: user.id,
            pan: p.pan.toUpperCase(),
            role: getRoleFromConstitution(constitution),
            is_primary: p.is_primary,
            mobile: p.is_primary ? (p.mobile || null) : null,
            sequence_no: i + 1,
          });
        }
      }

      // Persist consents
      await supabase.from('do_finance_consents').insert({
        application_id: applicationId,
        user_id: user.id,
        c1_gstin_pan_verify: true,
        c2_bureau_enquiry: true,
        c3_eligibility_not_loan: true,
        c4_authorised_signatory: true,
        c5_terms_agreed: true,
        legal_name_at_consent: gstinData?.legal_name || '',
        constitution_at_consent: constitution || '',
        terms_url_version: 'v1.0',
      });

      await streamDossier('live_dossier');
    } catch (err) {
      console.error('pipeline error:', err);
      setFailedReason(err.message || 'Evaluation failed. Please contact support.');
      setPhase('failed');
    }
  };

  // ── Helpers ────────────────────────────────────────────────────────────────

  const isUnsupported = () =>
    UNSUPPORTED_KEYWORDS.some(kw => constitution.toLowerCase().includes(kw));

  const streamDossier = async (targetPhase) => {
    setDossierData({});
    setVisibleKeys([]);
    setVerdict(null);
    setPhase(targetPhase);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/do-finance-eligibility`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
      });
      await readSSE(resp, (blk) => {
        if (blk.block === 'verdict') {
          setVerdict(blk.data);
          setVisibleKeys(prev => [...prev, 'verdict']);
        } else if (blk.block && blk.block !== 'error') {
          setDossierData(prev => ({ ...prev, [blk.block]: blk.data }));
          setVisibleKeys(prev => [...prev, blk.block]);
        }
      });
    } catch (err) {
      console.error('streamDossier error:', err);
    }
  };

  const handleRecheck = async () => {
    setRecheckLoading(true);
    setVisibleKeys([]);
    setDossierData({});
    setVerdict(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const resp = await fetch(`${SUPABASE_URL}/functions/v1/do-finance-eligibility`, {
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'apikey': SUPABASE_ANON_KEY,
        },
      });
      const ct = resp.headers.get('content-type') || '';
      if (ct.includes('text/event-stream')) {
        const blocks = {};
        let v = null;
        await readSSE(resp, (blk) => {
          if (blk.block === 'verdict') v = blk.data;
          else if (blk.block && blk.block !== 'error') blocks[blk.block] = blk.data;
        });
        setDossierData(blocks);
        setVerdict(v);
        const keys = Object.keys(blocks);
        keys.forEach((k, i) => setTimeout(() => setVisibleKeys(prev => [...prev, k]), i * 100));
        if (v) setTimeout(() => setVisibleKeys(prev => [...prev, 'verdict']), keys.length * 100);
      }
    } catch (err) {
      console.error('recheck error:', err);
    } finally {
      setRecheckLoading(false);
    }
  };

  // ── Dossier renderer ───────────────────────────────────────────────────────

  const renderDossier = () => {
    const d = dossierData;
    const vis = (key) => visibleKeys.includes(key);

    return (
      <>
        <DossierBlock icon="CreditCard" title="PAN Identity" visible={vis('pan_identity')}>
          {d.pan_identity && (
            <>
              <Row label="Name" value={d.pan_identity.name} />
              <Row label="PAN" value={maskPan(d.pan_identity.pan_masked)} />
              <Row label="Status" value={d.pan_identity.status} />
              <Row label="Aadhaar Linked" value={d.pan_identity.aadhaar_linked == null ? '—' : d.pan_identity.aadhaar_linked ? 'Yes' : 'No'} />
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="Building2" title="GSTIN" visible={vis('gstin')}>
          {d.gstin && (
            <>
              <Row label="Legal Name" value={d.gstin.legal_name} />
              <Row label="Status" value={d.gstin.status} />
              <Row label="Constitution" value={d.gstin.constitution} />
              <Row label="Taxpayer Type" value={d.gstin.taxpayer_type} />
              <Row label="Registered" value={d.gstin.registration_date} />
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="FileText" title="GSTR-3B Compliance" visible={vis('gstr3b_compliance')}>
          {d.gstr3b_compliance && (
            <>
              <Row label="Months Filed" value={d.gstr3b_compliance.months_filed ?? 'Not available'} />
              <Row label="Months Missing" value={d.gstr3b_compliance.months_missing ?? '—'} />
              <Row label="Regularity Score" value={d.gstr3b_compliance.regularity_score != null ? `${Math.round(d.gstr3b_compliance.regularity_score * 100)}%` : '—'} />
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="TrendingUp" title="Company Intelligence" visible={vis('company_intelligence')}>
          {d.company_intelligence && (
            <>
              <Row label="CIN Found" value={d.company_intelligence.cin_found ? 'Yes' : 'No'} />
              <Row label="Net Revenue" value={d.company_intelligence.net_revenue_range} />
              <Row label="Audit Adverse" value={d.company_intelligence.audit_adverse == null ? '—' : d.company_intelligence.audit_adverse ? 'Yes ⚠️' : 'No ✓'} />
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="Smartphone" title="Social Intelligence" visible={vis('social_intelligence')}>
          {d.social_intelligence && (
            <>
              <Row label="Connection Type" value={d.social_intelligence.is_postpaid == null ? '—' : d.social_intelligence.is_postpaid ? 'Postpaid' : 'Prepaid'} />
              <Row label="Circle" value={d.social_intelligence.circle} />
              <Row label="Mobile Tenure" value={d.social_intelligence.mobile_tenure_months != null ? `${d.social_intelligence.mobile_tenure_months}+ months` : '—'} />
              <Row label="Omniscore" value={d.social_intelligence.omniscore != null ? String(d.social_intelligence.omniscore) : '—'} />
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="BarChart2" title="Equifax Bureau" visible={vis('equifax_bureau')}>
          {d.equifax_bureau && (
            <>
              {d.equifax_bureau.score_not_found ? (
                <p style={{ fontSize: 13, color: '#6b7280', margin: 0 }}>No bureau record found. First-time credit applicant.</p>
              ) : (
                <>
                  <Row label="Credit Score" value={d.equifax_bureau.score != null ? String(d.equifax_bureau.score) : '—'} />
                  <Row label="Score Range" value={d.equifax_bureau.score_range} />
                  <Row label="Tradelines" value={d.equifax_bureau.tradeline_count != null ? String(d.equifax_bureau.tradeline_count) : '—'} />
                </>
              )}
            </>
          )}
        </DossierBlock>

        <DossierBlock icon="Package" title="SugarHathi Record" visible={vis('platform_trade')}>
          {d.platform_trade && (
            <>
              <Row label="Total Trades" value={String(d.platform_trade.total_trades || 0)} />
              <Row label="MT Facilitated" value={d.platform_trade.mt_facilitated != null ? `${Number(d.platform_trade.mt_facilitated).toFixed(1)} MT` : '—'} />
              <Row label="Cancellation Rate" value={d.platform_trade.cancellation_rate != null ? `${(d.platform_trade.cancellation_rate * 100).toFixed(1)}%` : '—'} />
              <Row label="TradeScore Band" value={d.platform_trade.trade_score_band} />
            </>
          )}
        </DossierBlock>

        {vis('verdict') && verdict && (
          <div style={{ display: 'flex', gap: 12, marginBottom: 16 }}>
            <SemiGauge label="Credit Score" value={verdict.equifax_score} max={900} band={verdict.sugar_score_band} empty={!verdict.equifax_score} />
            <SemiGauge label="TradeScore" value={verdict.trade_score} max={1000} band={verdict.trade_score_band} empty={!verdict.trade_score} />
          </div>
        )}

        {vis('verdict') && verdict && (
          <div style={{
            background: verdict.eligible ? '#f0fdf4' : '#fef2f2',
            border: `1.5px solid ${verdict.eligible ? '#bbf7d0' : '#fca5a5'}`,
            borderRadius: 16, padding: 20, marginBottom: 16,
            opacity: 1, transform: 'translateY(0)',
            transition: 'opacity 0.4s ease, transform 0.4s ease',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
              <Icon name={verdict.eligible ? 'CheckCircle' : 'XCircle'} size={26} color={verdict.eligible ? '#16a34a' : '#dc2626'} />
              <div>
                <p style={{ fontWeight: 800, fontSize: 16, color: '#1f2937', margin: 0 }}>
                  {verdict.eligible ? 'Eligible for DO Finance' : 'Not Currently Eligible'}
                </p>
                {verdict.eligible && (
                  <p style={{ fontSize: 13, color: '#166534', margin: 0 }}>
                    Bid limit: {verdict.bid_limit} vehicle{verdict.bid_limit !== 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>

            {/* Plain-language analysis — only shows bullets backed by real data */}
            <div style={{ background: '#fff', borderRadius: 14, padding: 14, marginBottom: 12 }}>
              {dossierData?.gstr3b_compliance?.months_filed != null && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                  <Icon name="Check" size={13} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 12.5, color: '#374151', margin: 0, lineHeight: 1.5 }}>
                    GSTR-3B filed {dossierData.gstr3b_compliance.months_filed} of 6 months{dossierData.gstr3b_compliance.months_missing ? `, ${dossierData.gstr3b_compliance.months_missing} missing` : ', no gaps'}.
                  </p>
                </div>
              )}
              {dossierData?.platform_trade?.total_trades != null && (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 8 }}>
                  <Icon name="Check" size={13} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 12.5, color: '#374151', margin: 0, lineHeight: 1.5 }}>
                    {dossierData.platform_trade.total_trades} trade{dossierData.platform_trade.total_trades !== 1 ? 's' : ''} on SugarHathi, {Math.round(dossierData.platform_trade.mt_facilitated ?? 0)} MT facilitated.
                  </p>
                </div>
              )}
              {verdict.equifax_score ? (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Icon name="Check" size={13} color="#16a34a" style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 12.5, color: '#374151', margin: 0, lineHeight: 1.5 }}>
                    Credit score in the {verdict.sugar_score_band ?? '—'} range.
                  </p>
                </div>
              ) : (
                <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                  <Icon name="Info" size={13} color="#9ca3af" style={{ flexShrink: 0, marginTop: 2 }} />
                  <p style={{ fontSize: 12.5, color: '#6b7280', margin: 0, lineHeight: 1.5 }}>
                    No bureau record found — first-time applicants are still assessed on trade history.
                  </p>
                </div>
              )}
            </div>

            {/* Bank ↔ GSTIN name-match panel */}
            {verdict.bank_gstin_name_match && (
              <div style={{
                background: verdict.bank_gstin_name_match === 'mismatch' ? '#fef2f2' : '#f9fafb',
                borderRadius: 14, padding: 14, marginBottom: 12,
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                  <span style={{ fontSize: 11.5, fontWeight: 700, color: '#6b7280' }}>Account Name Check</span>
                  <span style={{
                    fontSize: 10.5, fontWeight: 700, padding: '2px 9px', borderRadius: 20,
                    color: verdict.bank_gstin_name_match === 'mismatch' ? '#991b1b' : '#166534',
                    background: verdict.bank_gstin_name_match === 'mismatch' ? '#fee2e2' : '#dcfce7',
                  }}>
                    {verdict.bank_gstin_name_match === 'match' && 'Match'}
                    {verdict.bank_gstin_name_match === 'match_reordered' && 'Match'}
                    {verdict.bank_gstin_name_match === 'partial' && 'Under Review'}
                    {verdict.bank_gstin_name_match === 'mismatch' && 'Under Review'}
                  </span>
                </div>
                <div style={{ display: 'flex', gap: 16 }}>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', margin: 0, marginBottom: 2 }}>BANK ACCOUNT</p>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937', margin: 0 }}>{verdict.bank_holder_name || '—'}</p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: 10, color: '#9ca3af', margin: 0, marginBottom: 2 }}>GST REGISTRATION</p>
                    <p style={{ fontSize: 12.5, fontWeight: 700, color: '#1f2937', margin: 0 }}>{verdict.gstin_name_for_match || '—'}</p>
                  </div>
                </div>
              </div>
            )}

            {verdict.blacklisted && (
              <p style={{ fontSize: 13, color: '#991b1b', marginBottom: 12 }}>
                Access restricted until {verdict.blacklisted_until}. Contact support.
              </p>
            )}
            {verdict.eligible && (
              <button
                onClick={() => navigate('/buyer-operations-dashboard')}
                style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: GRAD, color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', boxShadow: '0 4px 16px rgba(234,88,12,0.25)' }}>
                Proceed to Bid →
              </button>
            )}
          </div>
        )}
      </>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════════
  // PHASE RENDERS
  // ═══════════════════════════════════════════════════════════════════════════

  // ── checking ───────────────────────────────────────────────────────────────
  if (phase === 'checking') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <style>{spin}</style>
      <div style={{ textAlign: 'center' }}>
        <Spinner size={36} />
        <p style={{ color: '#6b7280', fontSize: 14, marginTop: 14 }}>Checking your profile...</p>
      </div>
    </div>
  );

  // ── evaluating ─────────────────────────────────────────────────────────────
  if (phase === 'evaluating') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <style>{spin + dofResponsive}</style>
      <RoleBasedHeader />
      <div className="dof-wrap" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Spinner size={28} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Assessment in Progress</h2>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 4 }}>Your 13 verifications are running. Please check back shortly.</p>
        <p style={{ fontSize: 12, color: '#9ca3af' }}>Step: {evalStep}</p>
      </div>
    </div>
  );

  // ── failed ─────────────────────────────────────────────────────────────────
  if (phase === 'failed') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <style>{dofResponsive}</style>
      <div className="dof-wrap" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fee2e2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Icon name="XCircle" size={30} color="#dc2626" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Assessment Could Not Complete</h2>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>{failedReason}</p>
        <button
          onClick={() => setPhase('hero')}
          style={{ padding: '12px 28px', borderRadius: 12, border: 'none', background: GRAD, color: '#fff', fontWeight: 700, fontSize: 14, cursor: 'pointer', marginRight: 10 }}>
          Try Again
        </button>
        <button
          onClick={() => navigate(-1)}
          style={{ padding: '12px 28px', borderRadius: 12, border: '2px solid #e5e7eb', background: '#fff', color: '#6b7280', fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
          Go Back
        </button>
      </div>
    </div>
  );

  // ── hero ───────────────────────────────────────────────────────────────────
  if (phase === 'hero') {
    if (heroStep === 'usp') {
      return <UspIntro onInterested={() => setHeroStep('existing')} />;
    }

    const openBeforeApply = () => { setBeforeApplyOpen(true); setVideoNotice(false); };
    const openVideo = () => { setVideoNotice(true); setBeforeApplyOpen(false); };
    const closeBeforeApply = () => setBeforeApplyOpen(false);
    const confirmBeforeApply = () => { setBeforeApplyConfirmed(true); setBeforeApplyOpen(false); };

    // ── Terms & Conditions — dedicated full screen ──
    if (beforeApplyOpen) {
      return (
        <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
          <style>{dofResponsive}</style>
          <RoleBasedHeader />
          <div className="dof-wrap" style={{ padding: '32px 20px 40px' }}>
            <button
              onClick={closeBeforeApply}
              style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'none', border: 'none', color: '#9ca3af', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: 0, marginBottom: 18 }}>
              <Icon name="ArrowLeft" size={15} color="#9ca3af" />
              Back
            </button>

            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
              <LanguageSwitcher />
            </div>

            <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', color: '#c2410c', textTransform: 'uppercase', marginBottom: 6 }}>
              DO Finance
            </p>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1c1917', margin: 0, marginBottom: 10, lineHeight: 1.3 }}>
              {t('doFinanceTerms.pageTitle')}
            </h1>
            <p style={{ fontSize: 14, color: '#78716c', lineHeight: 1.6, margin: 0, marginBottom: 24 }}>
              {t('doFinanceTerms.pageSubtitle')}
            </p>

            <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 16, padding: '18px 20px', marginBottom: 20 }}>
              <p style={{ fontSize: 13.5, fontWeight: 700, color: '#9a3412', margin: 0, marginBottom: 10 }}>
                {t('doFinanceTerms.checklistTitle')}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {['checklistItem1', 'checklistItem2', 'checklistItem3', 'checklistItem4', 'checklistItem5'].map((key) => (
                  <div key={key} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
                    <Icon name="Check" size={14} color="#ea580c" style={{ marginTop: 3, flexShrink: 0 }} />
                    <p style={{ fontSize: 13, color: '#7c2d12', lineHeight: 1.5, margin: 0 }}>{t(`doFinanceTerms.${key}`)}</p>
                  </div>
                ))}
              </div>
            </div>

            <div style={{ background: '#fff', border: '1.5px solid #f0e9e0', borderRadius: 16, padding: '20px 20px', marginBottom: 20 }}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
                {['clause1', 'clause2', 'clause3', 'clause4', 'clause5'].map((key) => (
                  <div key={key}>
                    <p style={{ fontSize: 13.5, fontWeight: 700, color: '#1c1917', margin: 0, marginBottom: 4 }}>{t(`doFinanceTerms.${key}Heading`)}</p>
                    <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.6, margin: 0 }}>{t(`doFinanceTerms.${key}Body`)}</p>
                  </div>
                ))}
              </div>
            </div>

            <button
              onClick={confirmBeforeApply}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, width: '100%',
                padding: '16px 14px', borderRadius: 14, cursor: 'pointer', border: 'none',
                background: GRAD, boxShadow: '0 4px 16px rgba(234,88,12,0.3)',
              }}
            >
              <Icon name={beforeApplyConfirmed ? 'CheckCircle2' : 'Circle'} size={18} color="#fff" />
              <span style={{ fontSize: 15, fontWeight: 800, color: '#fff' }}>
                {t('doFinanceTerms.agreeButton')}
              </span>
            </button>
          </div>
        </div>
      );
    }

    const hubCards = [
      { key: 'video', icon: 'PlayCircle', title: 'View the Application Process', desc: 'A short walkthrough of the whole process, start to finish — 3 min.', step: 'First Step', gradient: 'linear-gradient(135deg,#fb923c,#ea580c)', shadow: 'rgba(234,88,12,0.20)', onClick: openVideo },
      { key: 'before', icon: 'ClipboardList', title: 'Terms & Conditions', desc: 'Assessment fee, verification consent, and credit facilitation terms.', step: beforeApplyConfirmed ? 'Completed' : 'Compulsory', gradient: 'linear-gradient(135deg,#ea580c,#9a3412)', shadow: 'rgba(154,52,18,0.24)', onClick: openBeforeApply },
      { key: 'start', icon: 'ArrowUpRight', title: 'Start My Application', desc: 'Payment, GST confirmation, PAN, and current account — about 8 minutes.', step: null, gradient: 'linear-gradient(135deg,#f97316,#c2410c)', shadow: 'rgba(194,65,12,0.22)', onClick: () => { if (!beforeApplyConfirmed) { setBeforeApplyOpen(true); return; } setColError(''); setScreen(1); setPhase('collection'); }, locked: !beforeApplyConfirmed },
    ];

    return (
      <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
        <style>{dofResponsive}</style>
        <RoleBasedHeader />
        <div className="dof-wrap" style={{ padding: '32px 20px 40px' }}>

          <p style={{ fontSize: 12, fontWeight: 700, letterSpacing: '0.05em', color: '#c2410c', textTransform: 'uppercase', marginBottom: 6 }}>
            DO Finance
          </p>
          <h1 style={{ fontSize: 23, fontWeight: 700, color: '#1c1917', margin: 0, marginBottom: 10, lineHeight: 1.3 }}>
            Let's get DO Finance
          </h1>
          <p style={{ fontSize: 14.5, color: '#78716c', lineHeight: 1.6, margin: 0, marginBottom: 28, maxWidth: 340 }}>
            Three steps, whenever you're ready. Nothing here is timed, and you can come back to any of them.
          </p>

          <div className="dof-hub-cards">
            {hubCards.map((c) => (
              <button
                key={c.key}
                onClick={c.onClick}
                style={{
                  textAlign: 'left', width: '100%',
                  border: '1.5px solid transparent', background: c.gradient, borderRadius: 18,
                  padding: '18px 20px 20px', cursor: 'pointer', display: 'flex', flexDirection: 'column', gap: 12,
                  boxShadow: `0 8px 20px ${c.shadow}`,
                  opacity: c.locked ? 0.6 : 1,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10 }}>
                  <div style={{
                    width: 38, height: 38, borderRadius: 12, flexShrink: 0,
                    display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(255,255,255,0.22)',
                  }}>
                    <Icon name={c.locked ? 'Lock' : c.icon} size={19} color="#fff" strokeWidth={1.8} />
                  </div>
                  {(c.step || c.locked) && (
                    <span style={{
                      fontSize: 10.5, fontWeight: 700, whiteSpace: 'nowrap',
                      letterSpacing: '0.02em', color: '#fff', background: 'rgba(255,255,255,0.22)',
                      padding: '3px 9px', borderRadius: 20,
                    }}>
                      {c.locked ? 'Agree to Terms first' : c.step}
                    </span>
                  )}
                </div>
                <div>
                  <p style={{ fontSize: 15.5, fontWeight: 700, margin: 0, marginBottom: 4, color: '#fff' }}>
                    {c.title}
                  </p>
                  <p style={{ fontSize: 13, lineHeight: 1.5, margin: 0, color: 'rgba(255,255,255,0.88)' }}>
                    {c.desc}
                  </p>
                </div>
              </button>
            ))}
          </div>

          {videoNotice && (
            <div style={{ marginTop: 16, background: '#fff', border: '1.5px solid #f0e9e0', borderRadius: 14, padding: 14, overflow: 'hidden' }}>
              <video
                controls
                playsInline
                style={{ width: '100%', borderRadius: 10, display: 'block', background: '#000' }}
              >
                <source src={DO_FINANCE_VIDEO_URL} type="video/mp4" />
              </video>
            </div>
          )}

          <div style={{ marginTop: 22, display: 'flex', alignItems: 'flex-start', gap: 10, padding: '4px 4px' }}>
            <div style={{ width: 20, height: 20, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
              <Icon name="Check" size={12} color="#16a34a" strokeWidth={3} />
            </div>
            <p style={{ fontSize: 12.5, color: '#1c1917', lineHeight: 1.6, margin: 0 }}>
              A non-refundable assessment fee of ₹999+taxes applies only when you START your application, not before.
            </p>
          </div>
        </div>
      </div>
    );
  }


  // ── collection ─────────────────────────────────────────────────────────────
  if (phase === 'collection') {
    const isProprietor = constitution.toLowerCase().includes('proprietor');
    const isPvtLtd = constitution.toLowerCase().includes('private') || constitution.toLowerCase().includes('opc');
    const isLLP = constitution.toLowerCase().includes('llp');
    const isPartnership = !!constitution && !isUnsupported() && !isProprietor && !isPvtLtd && !isLLP;
    const paymentDone = !!applicationId;

    const goBack = () => {
      setColError('');
      if (screen === 1) { setPhase('hero'); }
      else { setScreen(s => s - 1); }
    };

    return (
      <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
        <style>{spin + dofResponsive}</style>

        {/* Header */}
        <div style={{ background: GRAD, padding: '16px 20px 14px' }}>
          <div className="dof-wrap-flush" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
            <button
              onClick={goBack}
              style={{ background: 'rgba(255,255,255,0.2)', border: 'none', borderRadius: 8, width: 32, height: 32, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="ArrowLeft" size={16} color="#fff" />
            </button>
            <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 17, margin: 0 }}>Step {screen} of 6</h2>
          </div>
          <div className="dof-wrap-flush" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
            {[1, 2, 3, 4, 5, 6].map((s) => (
              <React.Fragment key={s}>
                <div style={{
                  width: s === screen ? 24 : 16, height: s === screen ? 24 : 16, borderRadius: '50%',
                  border: s === screen ? '2px solid #fff' : 'none',
                  background: s < screen ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.25)',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                  transition: 'all 0.3s ease',
                }}>
                  {s < screen && <Icon name="Check" size={10} color="#ea580c" strokeWidth={3} />}
                  {s === screen && <div style={{ width: 7, height: 7, borderRadius: '50%', background: '#fff' }} />}
                </div>
                {s < 6 && (
                  <div style={{ flex: 1, height: 2, background: s < screen ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.3)', transition: 'background 0.3s ease' }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        <div className="dof-wrap" style={{ padding: '20px 20px 60px' }}>
          {colError && (
            <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 10, padding: '10px 14px', marginBottom: 16 }}>
              <p style={{ color: '#991b1b', fontSize: 13, margin: 0 }}>{colError}</p>
            </div>
          )}

          {/* ── Screen 1: Payment ── */}
          {screen === 1 && (
            <div>
              {paymentDone ? (
                <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <div style={{ width: 56, height: 56, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <Icon name="CheckCircle" size={28} color="#16a34a" />
                  </div>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 6 }}>Payment Confirmed</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 24 }}>₹1,179 received · Continue your assessment</p>
                  <button
                    onClick={() => { setColError(''); setScreen(2); }}
                    style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: GRAD, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                    Continue →
                  </button>
                </div>
              ) : (
                <>
                  <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>Unlock Your DO Finance Bid Eligibility</h3>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Pay once to unlock your eligibility verdict. Non-refundable.</p>

                  <div style={{ background: '#fff7ed', border: '1.5px solid #fed7aa', borderRadius: 12, padding: '14px 16px', marginBottom: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end' }}>
                      <div>
                        <p style={{ fontSize: 11, color: '#92400e', margin: '0 0 2px' }}>Total payable</p>
                        <p style={{ fontSize: 28, fontWeight: 800, color: '#1f2937', margin: 0, lineHeight: 1 }}>₹1,179</p>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <p style={{ fontSize: 12, color: '#1f2937', fontWeight: 600, margin: 0 }}>₹999 + ₹180 GST</p>
                        <p style={{ fontSize: 11, color: '#1f2937', fontWeight: 600, margin: 0 }}>Non-refundable</p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handlePayment}
                    disabled={payLoading}
                    style={{
                      width: '100%', padding: '16px', borderRadius: 14, border: 'none',
                      background: payLoading ? '#e5e7eb' : GRAD,
                      color: payLoading ? '#9ca3af' : '#fff',
                      fontSize: 15, fontWeight: 800,
                      cursor: payLoading ? 'not-allowed' : 'pointer',
                      boxShadow: payLoading ? 'none' : '0 4px 16px rgba(234,88,12,0.3)',
                      marginBottom: 10,
                    }}>
                    {payLoading ? 'Opening payment...' : 'Pay ₹1,179 →'}
                  </button>
                  <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', margin: 0 }}>
                    🔒 Razorpay · UPI / Card / Net Banking
                  </p>
                </>
              )}
            </div>
          )}

          {/* ── Eligibility Checklist (replaces old Screens 2/3/4) ── */}
          {(screen === 2 || screen === 3 || screen === 4) && (
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>Eligibility Checklist</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Complete each step below to continue.</p>

              {/* Task 1: GSTIN Verification */}
              <div style={{ background: '#fff', border: `1.5px solid ${gstinData ? '#bbf7d0' : '#fed7aa'}`, borderRadius: 14, padding: 16, marginBottom: 12 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: (gstinData || gstinLoading || gstinError) ? 12 : 0 }}>
                  <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>1. GSTIN Verification</span>
                  {gstinData ? (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#16a34a', background: '#f0fdf4', padding: '3px 10px', borderRadius: 20 }}>Completed</span>
                  ) : (
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#ea580c', background: '#fff7ed', padding: '3px 10px', borderRadius: 20 }}>{gstinLoading ? 'Verifying...' : 'Pending'}</span>
                  )}
                </div>

                {gstinData ? (
                  isUnsupported() ? (
                    <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 14, padding: 16, textAlign: 'center' }}>
                      <Icon name="XCircle" size={28} color="#dc2626" />
                      <p style={{ fontWeight: 700, color: '#991b1b', marginTop: 8, fontSize: 14 }}>Constitution Not Supported</p>
                      <p style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>
                        DO Finance is available for Proprietorships, Partnerships, Private Limited, OPC, and LLP only.
                      </p>
                      <button
                        onClick={() => setPhase('hero')}
                        style={{ marginTop: 14, padding: '10px 24px', borderRadius: 10, border: 'none', background: GRAD, color: '#fff', fontWeight: 700, cursor: 'pointer' }}>
                        Go Back
                      </button>
                    </div>
                  ) : (
                    <div>
                      <p style={{ fontSize: 16, fontWeight: 800, color: '#1f2937', marginBottom: 2 }}>
                        {gstinData.trade_name || gstinData.legal_name}
                      </p>
                      {gstinData.trade_name && gstinData.legal_name !== gstinData.trade_name && (
                        <p style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>{gstinData.legal_name}</p>
                      )}
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 10 }}>
                        <Tag>{gstinData.gstin_status}</Tag>
                        <Tag color="#0369a1" bg="#f0f9ff">{gstinData.constitution}</Tag>
                        <Tag color="#6b7280" bg="#f9fafb">{gstinData.taxpayer_type}</Tag>
                      </div>
                      <button
                        onClick={() => navigate(-1)}
                        style={{ background: 'none', border: 'none', color: '#9ca3af', fontSize: 11, textDecoration: 'underline', cursor: 'pointer', padding: 0 }}>
                        Not your business? Go back
                      </button>
                    </div>
                  )
                ) : gstinError ? (
                  <div>
                    <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{gstinError}</p>
                    {profile?.gstin ? (
                      <button
                        onClick={() => { setGstinError(''); loadGstinData(profile?.gstin); }}
                        disabled={gstinLoading}
                        style={{
                          width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                          background: gstinLoading ? '#e5e7eb' : GRAD,
                          color: gstinLoading ? '#9ca3af' : '#fff',
                          fontSize: 15, fontWeight: 800,
                          cursor: gstinLoading ? 'not-allowed' : 'pointer',
                        }}>
                        {gstinLoading ? 'Retrying...' : 'Retry Verification →'}
                      </button>
                    ) : (
                      <button
                        onClick={() => navigate('/profile')}
                        style={{
                          width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                          background: GRAD, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer',
                        }}>
                        Update My Profile →
                      </button>
                    )}
                  </div>
                ) : (
                  <p style={{ fontSize: 13, color: '#6b7280' }}>Fetching your GSTIN details automatically...</p>
                )}
              </div>

              {/* Task 2 (Partnership): GST Document upload */}
              {gstinData && !isUnsupported() && isPartnership && (
                <div style={{ background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 14, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>2. Upload GST Document</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#ea580c', background: '#fff7ed', padding: '3px 10px', borderRadius: 20 }}>Pending</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                    Partnership firms are verified manually. Upload your GST registration document — our team will review it within 1 business day.
                  </p>

                  <div style={{ background: '#fafafa', borderRadius: 12, border: '1.5px dashed #fed7aa', padding: 14, marginBottom: 12, textAlign: 'center' }}>
                    <input
                      type="file" accept=".pdf,.jpg,.jpeg,.png"
                      onChange={e => { setPartnershipFile(e.target.files?.[0] || null); setPartnershipError(''); }}
                      style={{ width: '100%', fontSize: 13 }}
                    />
                    {partnershipFile && (
                      <p style={{ fontSize: 12, color: '#16a34a', marginTop: 8, fontWeight: 600 }}>{partnershipFile.name}</p>
                    )}
                  </div>

                  {partnershipError && (
                    <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 10 }}>{partnershipError}</p>
                  )}

                  <button
                    onClick={handlePartnershipSubmit}
                    disabled={partnershipUploading}
                    style={{
                      width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                      background: partnershipUploading ? '#e5e7eb' : GRAD,
                      color: partnershipUploading ? '#9ca3af' : '#fff',
                      fontSize: 15, fontWeight: 800,
                      cursor: partnershipUploading ? 'not-allowed' : 'pointer',
                    }}>
                    {partnershipUploading ? 'Uploading...' : 'Submit for Review →'}
                  </button>
                </div>
              )}

              {/* Task 2: PAN Numbers */}
              {gstinData && !isUnsupported() && !isPartnership && (
                <div style={{ background: '#fff', border: '1.5px solid #fed7aa', borderRadius: 14, padding: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: '#1f2937' }}>2. Enter PAN Numbers</span>
                    <span style={{ fontSize: 11, fontWeight: 700, color: '#ea580c', background: '#fff7ed', padding: '3px 10px', borderRadius: 20 }}>Pending</span>
                  </div>
                  <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 16 }}>
                    Enter your PAN and the mobile number linked to it.
                  </p>

                  {pansList.map((p, idx) => (
                    <div key={idx} style={{ background: '#fafafa', borderRadius: 12, border: '1.5px solid #fed7aa', padding: 14, marginBottom: 12 }}>
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>PAN NUMBER</label>
                      <input
                        type="text" value={p.pan} maxLength={10}
                        onChange={e => {
                          const v = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '');
                          setPansList(prev => prev.map((item, i) => i === idx ? { ...item, pan: v } : item));
                        }}
                        placeholder="e.g. ABCDE1234F"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontFamily: 'monospace', fontSize: 14, boxSizing: 'border-box', marginBottom: 10, outline: 'none' }}
                      />
                      <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>MOBILE LINKED TO THIS PAN</label>
                      <input
                        type="tel" value={p.mobile} maxLength={10}
                        onChange={e => {
                          const v = e.target.value.replace(/\D/g, '');
                          setPansList(prev => prev.map((item, i) => i === idx ? { ...item, mobile: v } : item));
                        }}
                        placeholder="10-digit mobile number"
                        style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1.5px solid #e5e7eb', fontSize: 14, boxSizing: 'border-box', outline: 'none' }}
                      />
                    </div>
                  ))}

                  <button
                    onClick={handlePansNext}
                    style={{ width: '100%', padding: '14px', borderRadius: 12, border: 'none', background: GRAD, color: '#fff', fontSize: 15, fontWeight: 800, cursor: 'pointer' }}>
                    Continue
                  </button>
                </div>
              )}
            </div>
          )}

          {/* ── Screen 5: Bank ── */}
          {screen === 5 && (
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>Business current account</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>
                Enter the current account details of this business GSTIN only — not a personal savings account.
              </p>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>CURRENT ACCOUNT NUMBER</label>
                <input
                  type="text" value={bankAccount}
                  onChange={e => { setBankAccount(e.target.value.replace(/\D/g, '')); setBankVerifyResult(null); }}
                  placeholder="Enter account number"
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 14, fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>

              <div style={{ marginBottom: 14 }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#374151', display: 'block', marginBottom: 4 }}>IFSC CODE</label>
                <input
                  type="text" value={bankIfsc} maxLength={11}
                  onChange={e => { setBankIfsc(e.target.value.toUpperCase()); setBankVerifyResult(null); }}
                  placeholder="e.g. HDFC0001234"
                  style={{ width: '100%', padding: '12px', borderRadius: 10, border: '1.5px solid #e5e7eb', fontSize: 14, fontFamily: 'monospace', boxSizing: 'border-box', outline: 'none' }}
                />
              </div>

              <button
                onClick={handleBankNext}
                style={{
                  width: '100%', padding: '14px', borderRadius: 12, border: 'none',
                  background: GRAD,
                  color: '#fff',
                  fontSize: 15, fontWeight: 800, cursor: 'pointer',
                }}>
                Continue
              </button>
            </div>
          )}

          {/* ── Screen 6: Consent ── */}
          {screen === 6 && (
            <div>
              <h3 style={{ fontSize: 18, fontWeight: 800, color: '#1f2937', marginBottom: 4 }}>Review & Authorise</h3>
              <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20 }}>Accept both declarations to submit your application.</p>

              <div
                onClick={() => setC1(!c1)}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 14, background: '#fff', borderRadius: 12, padding: 14, border: `1.5px solid ${c1 ? '#fed7aa' : '#f3f4f6'}`, cursor: 'pointer' }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${c1 ? '#ea580c' : '#d1d5db'}`, background: c1 ? '#ea580c' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  {c1 && <Icon name="Check" size={13} color="#fff" />}
                </div>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, margin: 0 }}>
                  I, the Authorised Signatory of M/s <strong>{gstinData?.legal_name || 'my business'}</strong>, confirm I am authorised to submit this application and consent to verification of business and personal details of all partners / directors as required. I accept full responsibility for this declaration.
                </p>
              </div>

              <div
                onClick={() => setC2(!c2)}
                style={{ display: 'flex', gap: 12, alignItems: 'flex-start', marginBottom: 24, background: '#fff', borderRadius: 12, padding: 14, border: `1.5px solid ${c2 ? '#fed7aa' : '#f3f4f6'}`, cursor: 'pointer' }}>
                <div style={{ width: 22, height: 22, borderRadius: 6, border: `2px solid ${c2 ? '#ea580c' : '#d1d5db'}`, background: c2 ? '#ea580c' : '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}>
                  {c2 && <Icon name="Check" size={13} color="#fff" />}
                </div>
                <p style={{ fontSize: 13, color: '#374151', lineHeight: 1.55, margin: 0 }}>
                  I have read and agree to the{' '}
                  <a href="/do-finance-terms" target="_blank" rel="noopener noreferrer" style={{ color: '#ea580c', textDecoration: 'underline' }} onClick={e => e.stopPropagation()}>
                    DO Finance Assessment Terms →
                  </a>
                  {' '}covering all verifications conducted during this process.
                </p>
              </div>

              <button
                onClick={handleSubmit}
                disabled={!c1 || !c2}
                style={{
                  width: '100%', padding: '16px', borderRadius: 14, border: 'none',
                  background: (!c1 || !c2) ? '#e5e7eb' : GRAD,
                  color: (!c1 || !c2) ? '#9ca3af' : '#fff',
                  fontSize: 15, fontWeight: 800,
                  cursor: (!c1 || !c2) ? 'not-allowed' : 'pointer',
                  boxShadow: (c1 && c2) ? '0 4px 16px rgba(234,88,12,0.3)' : 'none',
                }}>
                Submit Application →
              </button>
              <p style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                No additional payment · Assessment fee already paid
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── pipeline ───────────────────────────────────────────────────────────────
  if (phase === 'pipeline') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <style>{spin + dofResponsive}</style>
      <RoleBasedHeader />
      <div className="dof-wrap" style={{ padding: '40px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Spinner size={28} />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Running Your Assessment</h2>
        <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 24 }}>This takes 30–60 seconds. Please don't close this page.</p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, justifyContent: 'center', marginBottom: 20 }}>
          {PIPELINE_ITEMS.map(item => (
            <div key={item.key} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '6px 12px', borderRadius: 20, background: '#fff', border: '1px solid #f3f4f6' }}>
              <Icon name={item.icon} size={13} color="#ea580c" />
              <span style={{ fontSize: 12, color: '#374151', fontWeight: 500 }}>{item.label}</span>
            </div>
          ))}
        </div>
        <p style={{ fontSize: 12, color: '#9ca3af' }}>{pipelineStep}</p>
      </div>
    </div>
  );

  // ── live_dossier ───────────────────────────────────────────────────────────
  if (phase === 'live_dossier') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <style>{dofResponsive}</style>
      <div style={{ background: GRAD, padding: '20px' }}>
        <div className="dof-wrap">
          <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: 0 }}>Your Assessment Results</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, margin: '4px 0 0' }}>Live · One-time view only</p>
        </div>
      </div>
      <div className="dof-wrap" style={{ padding: '20px 16px 60px' }}>
        {renderDossier()}
      </div>
    </div>
  );

  // ── returning ──────────────────────────────────────────────────────────────
  if (phase === 'returning') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <style>{dofResponsive}</style>
      <div style={{ background: GRAD, padding: '20px' }}>
        <div className="dof-wrap">
          <h2 style={{ color: '#fff', fontWeight: 800, fontSize: 18, margin: 0 }}>Your Verified Profile</h2>
          <p style={{ color: 'rgba(255,255,255,0.8)', fontSize: 13, margin: '4px 0 0' }}>Assessment on file · Verified details below</p>
        </div>
      </div>
      <div className="dof-wrap" style={{ padding: '20px 16px 60px' }}>
        {renderDossier()}
        <button
          onClick={handleRecheck}
          disabled={recheckLoading}
          style={{
            width: '100%', padding: '12px', borderRadius: 12,
            border: `2px solid ${recheckLoading ? '#e5e7eb' : '#ea580c'}`,
            background: '#fff',
            color: recheckLoading ? '#9ca3af' : '#ea580c',
            fontWeight: 700, fontSize: 14,
            cursor: recheckLoading ? 'not-allowed' : 'pointer',
            marginTop: 8,
          }}>
          {recheckLoading ? 'Re-checking...' : '↻  Re-check Eligibility'}
        </button>
      </div>
    </div>
  );

  // ── partnership_pending ───────────────────────────────────────────────────
  if (phase === 'partnership_pending') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <div className="dof-wrap" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fff7ed', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Icon name="Clock" size={28} color="#ea580c" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Under Review</h2>
        <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 340, margin: '0 auto' }}>
          Your GST document has been submitted for manual review. Our team will get back to you within 1 business day.
        </p>
      </div>
    </div>
  );

  // ── partnership_approved ──────────────────────────────────────────────────
  if (phase === 'partnership_approved') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <div className="dof-wrap" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Icon name="CheckCircle" size={28} color="#16a34a" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Application Approved</h2>
        <p style={{ fontSize: 14, color: '#6b7280', maxWidth: 340, margin: '0 auto' }}>
          Your DO Finance limit will be activated shortly. You'll be notified once it's live.
        </p>
      </div>
    </div>
  );

  // ── partnership_rejected ──────────────────────────────────────────────────
  if (phase === 'partnership_rejected') return (
    <div style={{ minHeight: '100vh', background: '#fffbf5' }}>
      <RoleBasedHeader />
      <div className="dof-wrap" style={{ padding: '60px 20px', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: '#fef2f2', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px' }}>
          <Icon name="XCircle" size={28} color="#dc2626" />
        </div>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: '#1f2937', marginBottom: 8 }}>Application Rejected</h2>
        {partnershipReviewStatus?.decision_notes && (
          <p style={{ fontSize: 13, color: '#6b7280', maxWidth: 340, margin: '0 auto 20px' }}>{partnershipReviewStatus.decision_notes}</p>
        )}
        <button
          onClick={() => { setPartnershipFile(null); setPartnershipError(''); setPartnershipReviewStatus(null); setScreen(2); setPhase('collection'); }}
          style={{ padding: '12px 28px', borderRadius: 12, border: 'none', background: GRAD, color: '#fff', fontWeight: 800, fontSize: 14, cursor: 'pointer' }}>
          Resubmit Document
        </button>
      </div>
    </div>
  );

  return null;
};

export default DoFinanceApply;
